import { useState } from 'react'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { cn } from '@/lib/utils'
import { FileDown, LogOut, Upload } from 'lucide-react'

interface RoomHeaderProps {
  roomName?: string
  roomId?: string
  isGameCompleted: boolean
  isPolling: boolean
  onRefresh: () => void
  onInviteClick: () => void
  onConfigureClick: () => void
  onExportClick?: () => void
  onImportPlayersTeamsClick?: () => void
  onLeaveClick?: () => void
}

export const RoomHeader: React.FC<RoomHeaderProps> = ({
  roomName,
  roomId,
  isGameCompleted,
  isPolling,
  onRefresh,
  onInviteClick,
  onConfigureClick,
  onExportClick,
  onImportPlayersTeamsClick,
  onLeaveClick,
}) => {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = async () => {
    if (!roomId) return
    try {
      await navigator.clipboard.writeText(roomId)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3'>
        <h5 className='text-lg sm:text-2xl font-bold text-dark-blue whitespace-nowrap'>
          Game:
        </h5>
        <div className='bg-white px-3 py-1.5 rounded-lg border border-gray-200 min-w-0'>
          <span className='text-lg sm:text-xl font-semibold text-blue-600 break-words'>
            {roomName}
          </span>
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-2 sm:gap-3 justify-between w-full'>
        <div className='flex flex-wrap items-center gap-2 sm:gap-3 min-w-0'>
        <h5 className='text-sm sm:text-xl lg:text-lg font-bold text-dark-blue whitespace-nowrap'>
          Room ID:
        </h5>
        <div className='flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200 min-w-0 max-w-full sm:max-w-md lg:max-w-xl'>
          <code className='text-sm sm:text-base lg:text-base font-mono font-semibold text-blue-600 break-all'>
            {roomId}
          </code>
          <div
            className='relative w-4 h-4 sm:w-5 sm:h-5 cursor-pointer hover:scale-110 transition-transform flex-shrink-0'
            onClick={copyToClipboard}
          >
            <div
              className={cn(
                'absolute inset-0 transition-opacity duration-300',
                copied ? 'opacity-0' : 'opacity-100'
              )}
            >
              <img
                src='/icons/copy.svg'
                alt='Copy'
                className='w-full h-full'
              />
            </div>
            <div
              className={cn(
                'absolute inset-0 transition-opacity duration-300',
                copied ? 'opacity-100' : 'opacity-0'
              )}
            >
              <img
                src='/icons/copied.svg'
                alt='Copied'
                className='w-full h-full'
              />
            </div>
          </div>
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={cn(
                  'text-white bg-dark-blue hover:bg-dark-blue-hovered px-4 sm:px-5 py-2 sm:py-2.5 flex-shrink-0',
                  isGameCompleted && 'opacity-50 cursor-not-allowed'
                )}
                onClick={onConfigureClick}
                disabled={isGameCompleted}
              >
                <div className='flex items-center gap-2'>
                  <img
                    src='/icons/settings.svg'
                    alt='Configure'
                    className='h-[13px] w-[13px] sm:h-4 sm:w-4 flex-shrink-0'
                  />
                  <span className='text-sm sm:text-base'>Configure</span>
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Scenario & Events, Rounds & Timers, Communications, Resource
                Limits, Advisors, and World Setup
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div
          onClick={onRefresh}
          className='relative p-2 rounded-lg cursor-pointer bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 flex-shrink-0'
          title={isPolling ? 'Auto-refreshing active' : 'Refresh'}
        >
          <img
            src='/icons/refresh.svg'
            alt='Refresh'
            className='w-4 h-4 sm:w-5 sm:h-5'
          />
          {isPolling && (
            <div className='absolute w-2 h-2 bg-green-500 rounded-full -top-1 -right-1 animate-pulse border-2 border-white'></div>
          )}
        </div>

        {onExportClick && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className='flex items-center gap-2 flex-shrink-0 text-sm sm:text-base text-white bg-dark-blue hover:bg-dark-blue-hovered'
                  onClick={onExportClick}
                >
                  <Upload className='w-3 h-3 sm:w-4 sm:h-4' />
                  Export
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Export game data to JSON</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={cn(
                  'bg-gradient-to-r from-[#008BC0] to-[#0076c0] hover:from-[#0076c0] hover:to-[#006bb0] text-white font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 text-sm sm:text-base flex-shrink-0',
                  isGameCompleted &&
                    'opacity-50 cursor-not-allowed bg-gray-400 hover:bg-gray-400 hover:scale-100 hover:shadow-md'
                )}
                onClick={onInviteClick}
                disabled={isGameCompleted}
              >
                <img
                  src='/icons/share.svg'
                  alt='Share'
                  className='h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2'
                />
                Invite
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {isGameCompleted
                  ? 'Game completed - invitations are no longer available'
                  : 'Share this room with other participants to invite them to join the game'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {onLeaveClick && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  className='border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600 flex items-center gap-2 flex-shrink-0 text-sm sm:text-base'
                  onClick={onLeaveClick}
                >
                  <LogOut className='h-3 w-3 sm:h-4 sm:w-4' />
                  Leave Game
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Leave this game room</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        </div>

        {onImportPlayersTeamsClick && (
          <div className='flex-shrink-0'>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'flex items-center gap-2 flex-shrink-0 text-sm sm:text-base border-dark-blue text-dark-blue hover:bg-slate-50',
                      isGameCompleted && 'opacity-50 cursor-not-allowed'
                    )}
                    onClick={onImportPlayersTeamsClick}
                    disabled={isGameCompleted}
                  >
                    <FileDown className='w-3 h-3 sm:w-4 sm:h-4' />
                    Import players & teams
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {isGameCompleted
                      ? 'Game completed — section import is disabled'
                      : 'Merge players and teams from an export JSON file'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
      <div className='mt-3 w-[42.1rem]'>
        <p className='text-base text-black'>
          Before starting the game, please be sure to create 1 advisor for each
          of the people that are participating (including yourself using the
          Configure button, Advisors section)
        </p>
      </div>
    </div>
  )
}
